import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { employerAPI } from '../../services/api';
import { toast } from 'sonner';
import { User, ChevronLeft, Loader2, Send, Building } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useMessages } from '../../contexts/MessageContext';

const MessageThread = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { refreshUnreadCount } = useMessages();
  const [message, setMessage] = useState(null);
  const [thread, setThread] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const pollIntervalRef = useRef(null);
  const threadEndRef = useRef(null);

  const fetchThread = useCallback(async (initial = false) => {
    try {
      if (initial) setLoading(true);
      const res = await employerAPI.getMessage(id);
      setMessage(res.data.message);
      setThread(res.data.thread || []);
      // Backend marks message as read when fetched - refresh unread count
      refreshUnreadCount();
    } catch (error) {
      console.error('Error fetching thread:', error);
    } finally {
      if (initial) setLoading(false);
    }
  }, [id, refreshUnreadCount]);

  // Initial fetch and set up polling
  useEffect(() => {
    fetchThread(true);
    
    // Poll every 3 seconds for new messages
    pollIntervalRef.current = setInterval(() => {
      fetchThread(false);
    }, 3000);
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [fetchThread]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (threadEndRef.current) {
      threadEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [thread]);

  const handleSend = async () => {
    if (!reply.trim()) return;
    
    setSending(true);
    try {
      await employerAPI.replyToMessage(id, reply);
      setReply('');
      fetchThread();
      toast.success('Reply sent');
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  if (!message) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-[#0A0F1E]">Message not found</h2>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl space-y-6" data-testid="employer-message-thread">
        {/* Back Link */}
        <Link 
          to="/employer/messages" 
          className="inline-flex items-center gap-2 text-[#5B6472] hover:text-[#0A0F1E] transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
          Back to Messages
        </Link>

        {/* Header */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          <div className="flex items-center gap-4">
            {message.recipient_photo ? (
              <img src={message.recipient_photo} alt="" className="w-14 h-14 rounded-full object-cover border border-[#E8EDF5]" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-[#F5F8FF] border border-[#E8EDF5] flex items-center justify-center">
                <User className="w-6 h-6 text-[#5B6472]" />
              </div>
            )}
            <div>
              <h1 className="text-xl font-bold text-[#0A0F1E]">{message.recipient_name || 'Unknown'}</h1>
              <p className="text-[#5B6472] text-sm">
                {new Date(message.created_at).toLocaleString()}
              </p>
            </div>
          </div>
        </Card>

        {/* Messages */}
        <div className="space-y-4">
          {/* Original message (sent by employer) */}
          <div className="flex gap-3 flex-row-reverse">
            <div className="w-10 h-10 rounded-lg bg-[#1B5AFF]/20 flex items-center justify-center flex-shrink-0">
              <Building className="w-4 h-4 text-[#1B5AFF]" />
            </div>
            <Card className="flex-1 bg-[#1B5AFF]/10 border border-[#1B5AFF]/30 rounded-xl p-4">
              <p className="text-[#0A0F1E]">{message.content}</p>
              <p className="text-[#5B6472] text-xs mt-2">
                {new Date(message.created_at).toLocaleString()}
              </p>
            </Card>
          </div>

          {/* Thread replies */}
          {thread.map((msg) => (
            <div key={msg.id} className={`flex gap-3 ${msg.sender_id === user?.id ? 'flex-row-reverse' : ''}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                msg.sender_id === user?.id ? 'bg-[#1B5AFF]/20' : 'bg-[#F5F8FF] border border-[#E8EDF5]'
              }`}>
                {msg.sender_id === user?.id ? (
                  <Building className="w-4 h-4 text-[#1B5AFF]" />
                ) : (
                  <User className="w-4 h-4 text-[#5B6472]" />
                )}
              </div>
              <Card className={`flex-1 rounded-xl p-4 ${
                msg.sender_id === user?.id 
                  ? 'bg-[#1B5AFF]/10 border border-[#1B5AFF]/30' 
                  : 'bg-[#FFFFFF] border border-[#E8EDF5]'
              }`}>
                <p className="text-[#0A0F1E]">{msg.content}</p>
                <p className="text-[#5B6472] text-xs mt-2">
                  {new Date(msg.created_at).toLocaleString()}
                </p>
              </Card>
            </div>
          ))}
          {/* Scroll anchor */}
          <div ref={threadEndRef} />
        </div>

        {/* Reply Input */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4">
          <Textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Type your reply..."
            className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[100px] mb-4"
            data-testid="reply-input"
          />
          <div className="flex justify-end">
            <Button
              onClick={handleSend}
              disabled={sending || !reply.trim()}
              className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
              data-testid="send-reply-btn"
            >
              {sending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Send className="mr-2 h-4 w-4" />
              )}
              Send Reply
            </Button>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default MessageThread;
